import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form, Button, Header, Grid, Loader,
  CheckboxProps, Dimmer, Checkbox, Icon
} from 'semantic-ui-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../helpers/axiosInstance';
import { utils } from '../../helpers/utils';
import { OfficeModel } from '../../types/OfficeDTO';
import UserInfo from '../Common/SharedComponents/UserInfo';
import AppContext, { token_config } from '../../AppContext';
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';

type FormErrorFields = {
  officeDisplayName: string;
  email: string;
  replyEmail: string;
  phone: string;
};

export const OfficeForm: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useContext(AppContext);

  useEffect(() => {
    if (userInfo !== undefined) {
      const privileges: string[] = userInfo ? userInfo.Privileges.split(',') : [];
      if (!privileges.includes(token_config.MANAGE_OFFICES)) {
        navigate('/');
      }
    }
  }, [userInfo, navigate]);

  const { id } = useParams();

  let officeId = id ? parseInt(id) : 0;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<OfficeModel>({} as OfficeModel);
  const [formErrors, setFormErrors] = useState<FormErrorFields>({
    officeDisplayName: '',
    email: '',
    replyEmail: '',
    phone: ''
  });

  useEffect(() => {
    if (officeId > 0) {
      document.title = 'Edit Office - Adjure';
      fetchData();
    } else {
      document.title = 'Add Office - Adjure';
    }
  }, [officeId]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await axiosInstance.get(`/AdminSetting/GetOffice/${officeId}`).then(({ data, status }) => {
        if (status === 200) {
          setFormValues(data);
        }
        setIsLoading(false);
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const handlePhoneNumberInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const number = value.trim().replace(/[^0-9]/g, '');

    let formattedNumber = '';
    if (number.length < 4) {
      formattedNumber = number;
    } else if (number.length < 7) {
      formattedNumber = number.replace(/(\d{3})(\d{1})/, '$1-$2');
    } else if (number.length < 11) {
      formattedNumber = number.replace(/(\d{3})(\d{3})(\d{1})/, '$1-$2-$3');
    }
    setFormValues({ ...formValues, [name]: formattedNumber });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleCheckboxChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, field: string) => {
    setFormValues({ ...formValues, [field]: data.checked });
  };

  const validateImageDimensions = (file: any, fileInput: any): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width === 540 && img.height === 200) {
          resolve();
        } else {
          fileInput.value = "";
          reject("Image dimensions must be 540x200 pixels.");
        }
      };
      img.onerror = () => reject("Invalid image file.");
      img.src = URL.createObjectURL(file);
    });
  };

  const handleReplaceFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear the file input
    }
    setFormValues({ ...formValues, emailHeader: '' });
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    const file = event.target.files?.[0];
    if (file) {
      try {
        await validateImageDimensions(file, fileInput); // Validate dimensions
        const reader = new FileReader();
        reader.onload = () => {
          setFormValues({ ...formValues, emailHeader: reader.result as string });
        };
        reader.readAsDataURL(file); // Converts file to Base64
      } catch (validationError) {
        alert(validationError);
      }
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    validate(false, name, value);
  };

  const validate = async (validateAll: boolean, fieldName: string | null = null, value: any | null = null): Promise<FormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: FormErrorFields = { ...formErrors };
      try {
        if (validateAll) {
          errors = {
            officeDisplayName: !formValues?.officeDisplayName ? 'Name is required' : (!formValues.officeDisplayName.trim() ? 'Name is required' : ''),
            email: !formValues?.email ? 'Email is required' : (!utils.isValidEmail(formValues.email) ? 'Please provide a valid email' : ''),
            replyEmail: !formValues?.replyEmail ? 'From email is required' : (!utils.isValidEmail(formValues.replyEmail) ? 'Please provide a valid from email' : ''),
            phone: !formValues?.phone ? 'Phone is required' : (formValues?.phone && formValues?.phone.length < 12 ? 'Please enter a valid phone number which has at least 10 digits' : ''),
          };
        } else {
          switch (fieldName) {
            case 'officeDisplayName':
              errors.officeDisplayName = !value ? 'Name is required' : (!value.trim() ? 'Name is required' : '');
              break;
            case 'email':
              errors.email = !value ? 'Email is required' : (!utils.isValidEmail(value) ? 'Please provide a valid email' : '');
              break;
            case 'replyEmail':
              errors.replyEmail = !value ? 'From email is required' : (!utils.isValidEmail(value) ? 'Please provide a valid from email' : '');
              break;
            case 'phone':
              errors.phone = !value ? 'Phone is required' : (value && value.length < 12 ? 'Please enter a valid phone number which has at least 10 digits' : '');
              break;
            default:
              break;
          }
        }
        setFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  const handleSubmit = async () => {
    const errors = await validate(true);
    const hasErrors = Object.values(errors).some((error) => error);
    if (!hasErrors) {
      // handle form submission 
      let payload = { ...formValues };
      if (payload.officeId === undefined) {
        payload.officeId = 0;
      }
      if (payload.isActive === undefined) {
        payload.isActive = false;
      }
      payload.officeDisplayName = payload.officeDisplayName ? payload.officeDisplayName.trim() : '';
      axiosInstance.post('/AdminSetting/AddUpdateOffice', payload).then(({ data, status, statusText }) => {
        if (status === 200) {
          if (officeId === 0) {
            toast.success('Office added successfully');
          } else {
            toast.success('Office updated successfully');
          }
          navigate('/offices');
        }
      });
    }
  };

  return (
    <section className="section" style={{
      minHeight: '100vh'
    }}>
      <div className="section__head">
        <div className="section__head-content">
          <Header as="h1">{officeId === 0 ? 'Add' : 'Edit'} Office</Header>
        </div>
        <div className="section__actions">
          <UserInfo />
          <SwitchTheme />
        </div>
      </div>
      <div className='section__body'>
        {
          <Dimmer.Dimmable as="div" dimmed={isLoading}>
            <Dimmer active={isLoading} inverted={true}>
              <Loader>Loading</Loader>
            </Dimmer>
            <Grid padded>
              <Grid.Row>
                <Grid.Column mobile={16} tablet={8} computer={8}>
                  <Form className='form-custom'>
                    <Form.Field>
                      <Form.Input
                        label="Name"
                        name="officeDisplayName"
                        value={formValues.officeDisplayName}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.officeDisplayName}
                        required
                        maxLength={100}
                      />
                      {formValues.officeDisplayName?.length === 100 && <div className="error-message" style={{ color: 'red' }}>Maximum character limit exceeded</div>}
                      {formErrors?.officeDisplayName && <div className="error-message" style={{ color: 'red' }}>{formErrors?.officeDisplayName}</div>}
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        label="Email"
                        name="email"
                        value={formValues.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.email}
                        required
                      />
                      {formErrors?.email && <div className="error-message" style={{ color: 'red' }}>{formErrors?.email}</div>}
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        label="From Email"
                        name="replyEmail"
                        value={formValues.replyEmail}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.replyEmail}
                        required
                      />
                      {formErrors?.replyEmail && <div className="error-message" style={{ color: 'red' }}>{formErrors?.replyEmail}</div>}
                    </Form.Field>
                    <Form.Field>
                      <Form.Input
                        label="Phone"
                        name="phone"
                        value={formValues.phone}
                        onChange={handlePhoneNumberInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.phone}
                        required
                        maxLength={12}
                      />
                      {formErrors?.phone && <div className="error-message" style={{ color: 'red' }}>{formErrors?.phone}</div>}
                    </Form.Field>
                    <Form.Field style={{ position: 'relative' }}>
                    <span style={{ color: '#F67549', position: 'absolute', top: '0.3rem', left: '13rem', fontSize: '1.3rem' }}>(Upload image with dimensions: 540 W x 200 H pixels)</span>
                      <Form.Input
                        type="file"
                        accept="image/*"
                        label="Email Header"
                        onChange={handleFileChange}
                        input={{ ref: fileInputRef }}
                      />
                    </Form.Field>
                    {formValues.emailHeader && (
                      <div style={{ position: 'relative', top: '-1rem', marginBottom: '0.5rem' }}>
                        <img src={formValues.emailHeader} style={{ width: '40%' }} />
                        <Icon
                          name="trash"
                          color="red"
                          onClick={handleReplaceFile}
                          style={{ position: 'relative', right: '-1rem', cursor: 'pointer' }}
                        />
                      </div>
                    )}
                    <Form.Field>
                      <Form.Input
                        label="Email Preheader"
                        name="emailPreHeader"
                        value={formValues.emailPreHeader}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Checkbox
                        label='Active'
                        value={'true'}
                        checked={formValues.isActive === true}
                        onChange={(e, data) => handleCheckboxChange(e, data, 'isActive')}
                        onBlur={handleBlur}
                        style={{
                          margin: '1rem 1rem 0rem 1rem',
                          fontSize: '1.6rem'
                        }}
                      />
                    </Form.Field>
                    <Grid>
                      <Grid.Column>
                        <Button type="submit" onClick={handleSubmit} className='violet right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>
                          {officeId === 0 ? 'Save' : 'Update'}
                        </Button> {' '}
                        <Button type="button" className='second right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }} onClick={() => navigate('/offices')}>
                          Cancel
                        </Button>
                      </Grid.Column>
                    </Grid>
                  </Form>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <br />
          </Dimmer.Dimmable>
        }
      </div>
    </section>
  );
}

export default OfficeForm;